import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import moment from 'moment';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';

import { getOriginalMonthlyPriceInCents } from '../../../../../CoachTools/Model/UpsellConfig';
import { formatCurrencyCents } from '../../../../../utils/formatters';
import { DateFormat } from '../../../../../utils/date';
import texts from './texts.json';
import {
  Container,
  HeaderRow,
  Title,
  ActionButton,
  Details,
  OriginalPrice,
} from './styles';

/**
 * The 1:1 a client accepted on the challenge's landing page, shown on the payment step with the coach's offer: its
 * price (struck through from its original price when it has one), its start, and its commitment (cancel at any time,
 * or the minimum months it binds the client to). Removed, it says so and can be added back.
 */
const UpsellAddedCard = ({
  offer,
  challengeEndDate,
  isAdded,
  onRemove,
  onAddBack,
  onShown,
  isDarkTheme,
}) => {
  useEffect(() => {
    onShown();
  }, [onShown]);

  if (!isAdded) {
    return (
      <Container $isDarkTheme={isDarkTheme} aria-label={texts.notAdded}>
        <HeaderRow>
          <Title $isDarkTheme={isDarkTheme}>
            <CloseIcon fontSize="small" color="error" aria-hidden="true" />
            {texts.notAdded}
          </Title>
          <ActionButton type="button" onClick={onAddBack} $isDarkTheme={isDarkTheme}>{texts.addBack}</ActionButton>
        </HeaderRow>
      </Container>
    );
  }

  const originalPriceInCents = getOriginalMonthlyPriceInCents(offer);
  const startDetails = format(texts.startDetails, {
    startDate: moment(challengeEndDate).format(DateFormat.MONTH_DAY_FORMAT),
  });
  // A 1-month 1:1 can be cancelled at any time; a longer one says how long the client commits to.
  const minMonths = offer.minSubscriptionMonths || 1;
  const commitment = minMonths === 1 ? texts.cancelAnyTime : format(texts.minimum, { months: minMonths });

  return (
    <Container $isDarkTheme={isDarkTheme} aria-label={texts.added}>
      <HeaderRow>
        <Title $isDarkTheme={isDarkTheme}>
          <CheckIcon fontSize="small" color="success" aria-hidden="true" />
          {texts.added}
        </Title>
        <ActionButton type="button" onClick={onRemove} $isDarkTheme={isDarkTheme}>{texts.remove}</ActionButton>
      </HeaderRow>
      <Details>
        {!!originalPriceInCents && (
          <>
            <OriginalPrice>{formatCurrencyCents(originalPriceInCents, offer.currency)}</OriginalPrice>
            {' '}
          </>
        )}
        {format(texts.price, { price: formatCurrencyCents(offer.monthlyPriceInCents, offer.currency) })}
        {` ${startDetails}`}
        {` ${commitment}`}
      </Details>
    </Container>
  );
};

UpsellAddedCard.propTypes = {
  offer: PropTypes.shape({
    monthlyPriceInCents: PropTypes.number.isRequired,
    originalMonthlyPriceInCents: PropTypes.number,
    minSubscriptionMonths: PropTypes.number,
    currency: PropTypes.string,
  }).isRequired,
  challengeEndDate: PropTypes.string,
  isAdded: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAddBack: PropTypes.func.isRequired,
  onShown: PropTypes.func,
  isDarkTheme: PropTypes.bool,
};

UpsellAddedCard.defaultProps = {
  challengeEndDate: null,
  onShown: () => {},
  isDarkTheme: false,
};

export default UpsellAddedCard;
